import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "./lib/supabase";
import { find } from "world-universities-search";
import AppHeader from "./components/AppHeader";
import ThemedSelect from "./components/ThemedSelect";

const AuthPage = () => {
  const navigate = useNavigate();

  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [university, setUniversity] = useState("");
  const [otherUniversity, setOtherUniversity] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);

  const passwordChecks = React.useMemo(
    () => ({
      minLength: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[^A-Za-z0-9]/.test(password),
    }),
    [password]
  );

  const isPasswordStrong = Object.values(passwordChecks).every(Boolean);
  const isConfirmMatch = confirmPassword.length > 0 && password === confirmPassword;

  const universityOptions = React.useMemo(() => {
    const broadMatches = [...find("university"), ...find("college"), ...find("institute")];
    const uniqueSorted = Array.from(new Set(broadMatches)).sort((a, b) => a.localeCompare(b));
    return [...uniqueSorted, "Other"];
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setLoading(true);

    const trimmedEmail = email.trim();

    if (!trimmedEmail || !password) {
      setErrorMessage("Please enter both email and password.");
      setLoading(false);
      return;
    }

    if (mode === "signup") {
      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();
      const trimmedOtherUniversity = otherUniversity.trim();
      const selectedUniversity = university === "Other" ? trimmedOtherUniversity : university;

      if (!trimmedFirstName || !trimmedLastName || !confirmPassword || !university) {
        setErrorMessage("Please complete all sign up fields.");
        setLoading(false);
        return;
      }

      if (password !== confirmPassword) {
        setErrorMessage("Passwords do not match.");
        setLoading(false);
        return;
      }

      if (!isPasswordStrong) {
        setErrorMessage("Please make sure your password meets all requirements.");
        setLoading(false);
        return;
      }

      if (university === "Other" && !trimmedOtherUniversity) {
        setErrorMessage("Please enter your university name.");
        setLoading(false);
        return;
      }

      if (!selectedUniversity) {
        setErrorMessage("Please select a university.");
        setLoading(false);
        return;
      }
    }

    if (!supabase || !supabase.auth) {
      setErrorMessage(
        "Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in frontend/.env and restart the app."
      );
      setLoading(false);
      return;
    }

    let response;

    if (mode === "signin") {
      response = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password,
      });
    } else {
      const selectedUniversity = university === "Other" ? otherUniversity.trim() : university;
      response = await supabase.auth.signUp({
        email: trimmedEmail,
        password,
        options: {
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            university_name: selectedUniversity,
          },
        },
      });
    }

    const { error } = response;

    if (error) {
      setErrorMessage(error.message);
      setLoading(false);
      return;
    }

    if (mode === "signup") {
      setSuccessMessage("Sign up successful. Check your email for verification.");
      setLoading(false);
      return;
    }

    setLoading(false);
    navigate("/onboarding");
  };

  return (
    <div className="min-h-screen bg-secondary">
      <AppHeader titleSize="text-5xl" />
      <main className="px-6 py-12 pt-20">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-primary mb-6 text-center">Get Started with DeepToast</h2>

          <div className="grid grid-cols-2 gap-2 mb-6 bg-slate-100 p-1 rounded-lg">
            <button
              type="button"
              onClick={() => setMode("signin")}
              className={`py-2 rounded-md font-semibold transition ${
                mode === "signin" ? "bg-primary text-white" : "text-slate-700"
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => setMode("signup")}
              className={`py-2 rounded-md font-semibold transition ${
                mode === "signup" ? "bg-primary text-white" : "text-slate-700"
              }`}
            >
              Sign Up
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "signup" ? (
              <>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">First Name</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    placeholder="Enter first name"
                    className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    placeholder="Enter last name"
                    className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </>
            ) : null}

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  if (mode === "signup") {
                    setPasswordTouched(true);
                  }
                }}
                placeholder="Enter password"
                className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {mode === "signup" && passwordTouched ? (
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
                <p className={passwordChecks.minLength ? "text-emerald-600" : "text-red-600"}>
                  {passwordChecks.minLength ? "✓" : "✗"} At least 8 characters
                </p>
                <p className={passwordChecks.uppercase ? "text-emerald-600" : "text-red-600"}>
                  {passwordChecks.uppercase ? "✓" : "✗"} At least one uppercase letter
                </p>
                <p className={passwordChecks.lowercase ? "text-emerald-600" : "text-red-600"}>
                  {passwordChecks.lowercase ? "✓" : "✗"} At least one lowercase letter
                </p>
                <p className={passwordChecks.number ? "text-emerald-600" : "text-red-600"}>
                  {passwordChecks.number ? "✓" : "✗"} At least one number
                </p>
                <p className={passwordChecks.specialChar ? "text-emerald-600" : "text-red-600"}>
                  {passwordChecks.specialChar ? "✓" : "✗"} At least one special character
                </p>
              </div>
            ) : null}

            {mode === "signup" ? (
              <>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Re-enter Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => {
                      setConfirmPassword(event.target.value);
                      setConfirmPasswordTouched(true);
                    }}
                    placeholder="Re-enter password"
                    className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                {confirmPasswordTouched ? (
                  <p className={`text-sm ${isConfirmMatch ? "text-emerald-600" : "text-red-600"}`}>
                    {isConfirmMatch ? "Passwords match" : "Passwords do not match"}
                  </p>
                ) : null}

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">University Name</label>
                  <ThemedSelect
                    value={university}
                    onChange={(value) => setUniversity(value)}
                    options={universityOptions}
                    placeholder="Select your university"
                    searchable
                    size="medium"
                    variant="plain"
                    className="w-full"
                  />
                </div>

                {university === "Other" ? (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Enter University Name</label>
                    <input
                      type="text"
                      value={otherUniversity}
                      onChange={(event) => setOtherUniversity(event.target.value)}
                      placeholder="Enter your university"
                      className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                ) : null}
              </>
            ) : null}

            {errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}
            {successMessage ? <p className="text-sm text-emerald-600">{successMessage}</p> : null}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white py-2 rounded-md font-semibold hover:opacity-95 disabled:opacity-70"
            >
              {loading ? "Please wait..." : mode === "signin" ? "Sign In" : "Create Account"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AuthPage;
